import api from './api';

/**
 * Match-Alarm (Marcel 2026-10-24): Glocke je Pro-Spiel wie bei OneFootball.
 * Backend: GET /api/pro/match-alerts, POST/DELETE /api/pro/events/:id/alert.
 * Die Pushes kommen ausschließlich in der DartCloud-App an (das Web hat keinen Push) –
 * `hasDevice` sagt, ob der Nutzer schon ein App-Gerät verknüpft hat.
 *
 * Ein kleiner Modul-Store hält die aktiven Glocken des eingeloggten Nutzers, damit
 * jede Liste (Spiele, Startseite, Turnierseite, Spielerseite) denselben Stand zeigt.
 */
export interface MatchAlertList {
  eventIds: string[];
  ids: string[];
  hasDevice: boolean;
  pushEnabled: boolean;
}

export interface MatchAlertSetResult {
  active: boolean;
  eventId: string | null;
  flashliveEventId: string;
  hasDevice: boolean;
  pushEnabled: boolean;
}

type Listener = () => void;

interface Store {
  token: string | null;
  loaded: boolean;
  loading: Promise<void> | null;
  keys: Set<string>;
  hasDevice: boolean | null;
  pushEnabled: boolean | null;
  version: number;
  listeners: Set<Listener>;
}

const store: Store = {
  token: null,
  loaded: false,
  loading: null,
  keys: new Set(),
  hasDevice: null,
  pushEnabled: null,
  version: 0,
  listeners: new Set(),
};

const emit = () => {
  store.version += 1;
  store.listeners.forEach((l) => l());
};

const normalizeKey = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Ist für eine der übergebenen IDs (Mongo-_id oder flashliveEventId) eine Glocke aktiv? */
export function isActive(keys: Array<string | null | undefined>): boolean {
  for (const k of keys) {
    const key = normalizeKey(k);
    if (key && store.keys.has(key)) return true;
  }
  return false;
}

export function getVersion(): number {
  return store.version;
}

export function getDeviceInfo(): { hasDevice: boolean | null; pushEnabled: boolean | null } {
  return { hasDevice: store.hasDevice, pushEnabled: store.pushEnabled };
}

export function subscribe(listener: Listener): () => void {
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

/** Lokalen Stand verwerfen (Logout / Nutzerwechsel) */
export function reset(): void {
  store.token = null;
  store.loaded = false;
  store.loading = null;
  store.keys = new Set();
  store.hasDevice = null;
  store.pushEnabled = null;
  emit();
}

/**
 * Glocken des Nutzers laden (einmal je Token; parallele Aufrufe teilen sich die Anfrage).
 * Ohne Token wird nichts geladen.
 */
export async function load(token: string | null | undefined, force = false): Promise<void> {
  const tk = normalizeKey(token);
  if (!tk) {
    if (store.token || store.keys.size) reset();
    return;
  }
  if (store.token !== tk) {
    store.token = tk;
    store.loaded = false;
    store.loading = null;
    store.keys = new Set();
  }
  if (store.loaded && !force) return;
  if (store.loading) return store.loading;
  store.loading = (async () => {
    try {
      const res = await api.get<MatchAlertList>('/api/pro/match-alerts');
      const data = res.data ?? ({} as MatchAlertList);
      const keys = new Set<string>();
      for (const id of [...(data.eventIds ?? []), ...(data.ids ?? [])]) {
        const key = normalizeKey(id);
        if (key) keys.add(key);
      }
      store.keys = keys;
      store.hasDevice = data.hasDevice ?? null;
      store.pushEnabled = data.pushEnabled ?? null;
      store.loaded = true;
      emit();
    } finally {
      store.loading = null;
    }
  })();
  return store.loading;
}

/** Glocke setzen/entfernen; aktualisiert den Store für alle Listen */
export async function setAlert(id: string, active: boolean): Promise<MatchAlertSetResult> {
  const res = active
    ? await api.post<MatchAlertSetResult>(`/api/pro/events/${encodeURIComponent(id)}/alert`, { active: true })
    : await api.delete<MatchAlertSetResult>(`/api/pro/events/${encodeURIComponent(id)}/alert`);
  const data = res.data;
  const keys = [id, data?.eventId, data?.flashliveEventId].map(normalizeKey).filter((k): k is string => Boolean(k));
  for (const key of keys) {
    if (data?.active) store.keys.add(key);
    else store.keys.delete(key);
  }
  if (typeof data?.hasDevice === 'boolean') store.hasDevice = data.hasDevice;
  if (typeof data?.pushEnabled === 'boolean') store.pushEnabled = data.pushEnabled;
  emit();
  return data;
}

/** Nur für Tests */
export function __resetForTests(): void {
  reset();
  store.listeners.clear();
  store.version = 0;
}

const matchAlertService = { isActive, getVersion, getDeviceInfo, subscribe, reset, load, setAlert };
export default matchAlertService;
